import { useState, useEffect } from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContentText from '@mui/material/DialogContentText';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import Stack from '@mui/material/Stack';
import Stepper from '@mui/material/Stepper';
import Step from '@mui/material/Step';
import StepLabel from '@mui/material/StepLabel';
import useMediaQuery from '@mui/material/useMediaQuery';
import { LoadingButton } from '@mui/lab';
import GradingIcon from '@mui/icons-material/Grading';
import SendIcon from '@mui/icons-material/Send';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckIcon from '@mui/icons-material/Check';
import { useTheme } from '@mui/material/styles';
import { useOfferTermsApi } from '../api';
import { useAlert, LoadingScreen } from '@lendica/components';
import NumberFormat from 'react-number-format';
import { OfferTermsInvitationDetail, OfferTermsInvitation } from '@lendica/utils';
import { useEmailVerificationGate } from '@lendica/hooks';

export interface InviteDialogProps {
    open: boolean;
    activeStep?: InviteDialogStep;
    setActiveStep: (step: InviteDialogStep) => void;
    selectedRow?: OfferTermsInvitation;
    handleClose: () => void;
    onSent?: () => Promise<void> | void;
}

export enum InviteDialogStep {
    CustomerInformation = 0,
    ReviewInvitation = 1,
}

interface CustomerInformation {
    email: string;
    company_name: string;
    ein?: string;
}

export const InviteDialog = ({
    open,
    activeStep,
    setActiveStep,
    selectedRow,
    handleClose,
    onSent,
}: InviteDialogProps) => {
    const [loading, setLoading] = useState(false);
    const [customer, setCustomer] = useState<CustomerInformation>();
    const [invitation, setInvitation] = useState<OfferTermsInvitationDetail>();
    const steps = ['Customer information', 'Review invitation'];

    const { setSuccessMessage, setErrorMessage } = useAlert();
    const theme = useTheme();
    const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
    const { submitInvitation, resendInvitation, previewInvitation, getInvitationByID } =
        useOfferTermsApi();
    const { requireVerification } = useEmailVerificationGate();

    const handleBack = () => {
        setActiveStep(InviteDialogStep.CustomerInformation);
    };

    const handleNext = async () => {
        setLoading(true);
        try {
            const invitationDetails = await previewInvitation(
                customer?.company_name || '',
                customer?.email || ''
            );
            setInvitation(invitationDetails);
            setActiveStep(InviteDialogStep.ReviewInvitation);
            setLoading(false);
            onSent?.();
        } catch (error: any) {
            setErrorMessage(
                error?.response?.data?.message ||
                    error?.response?.data?.error ||
                    'Failed to submit preview invitation.'
            );
            setLoading(false);
        }
    };

    const handleSendInvites = async () => {
        setLoading(true);

        try {
            const response = await submitInvitation(
                invitation?.id || '',
                invitation?.company_name || '',
                invitation?.email || '',
                invitation?.email_content || ''
            );
            setSuccessMessage(response.message || 'Invitation sent.');
            if (onSent) {
                await onSent();
            }
            setTimeout(() => {
                setLoading(false);
                setCustomer(undefined);
                handleClose();
            }, 800);
        } catch (error: any) {
            const status = error?.response?.status;
            if (status === 403) {
                requireVerification(error?.response?.data);
                handleClose();
            }
            setErrorMessage(error?.response?.data?.message || 'Failed to send invitations.');
            setLoading(false);
        }
    };

    const handleResendInvites = async () => {
        setLoading(true);
        try {
            const response = await resendInvitation(invitation?.id || '');
            setSuccessMessage(response.message || 'Invitation resent.');
            if (onSent) {
                await onSent();
            }
            setTimeout(() => {
                setLoading(false);
                setCustomer(undefined);
                handleClose();
            }, 800);
        } catch (error: any) {
            setErrorMessage(error?.response?.data?.message || 'Failed to resend invitations.');
            setLoading(false);
        }
    };

    const getInvitationDetails = async (invitationId: string) => {
        setLoading(true);
        try {
            const invitationDetails = await getInvitationByID(invitationId);
            setInvitation(invitationDetails);
            setTimeout(() => {
                setLoading(false);
            }, 100);
        } catch (error: any) {
            setErrorMessage(error?.response?.data?.message || 'Failed to get invitation details.');
            setLoading(false);
        }
    };

    useEffect(() => {
        if (activeStep === InviteDialogStep.ReviewInvitation && selectedRow) {
            (async () => {
                await getInvitationDetails(selectedRow.id);
            })();
        }
    }, [activeStep]);

    return (
        <Dialog
            fullScreen={fullScreen}
            maxWidth={activeStep === InviteDialogStep.ReviewInvitation ? 'md' : 'sm'}
            open={open}
            onClose={handleClose}
            PaperProps={{
                component: 'form',
                sx: {
                    paddingY: 1,
                    paddingX: 1,
                },
            }}
        >
            <DialogTitle>Invite customers</DialogTitle>
            <Box sx={{ width: '100%', my: 1 }}>
                <Stepper activeStep={activeStep} alternativeLabel>
                    {steps.map(label => (
                        <Step key={label}>
                            <StepLabel>{label}</StepLabel>
                        </Step>
                    ))}
                </Stepper>
            </Box>
            {loading && (
                <Box
                    sx={{
                        width: '100%',
                        height: '100%',
                        minHeight: '320px',
                        minWidth: '600px',
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                    }}
                >
                    <LoadingScreen />
                </Box>
            )}
            {activeStep === InviteDialogStep.CustomerInformation && (
                <CustomerInformationStep customer={customer!} setCustomer={setCustomer} />
            )}
            {!loading && activeStep === InviteDialogStep.ReviewInvitation && (
                <ReviewInvitationStep customer={customer!} invitation={invitation!} />
            )}
            <DialogActions>
                {activeStep === InviteDialogStep.CustomerInformation && (
                    <>
                        <Button onClick={handleClose} color="secondary">
                            Cancel
                        </Button>
                        <LoadingButton
                            loading={loading}
                            onClick={handleNext}
                            autoFocus
                            variant="contained"
                            color="primary"
                            disabled={!customer?.email || !customer?.company_name}
                            startIcon={<GradingIcon />}
                        >
                            Review invitation
                        </LoadingButton>
                    </>
                )}
                {activeStep === InviteDialogStep.ReviewInvitation && (
                    <>
                        <Button onClick={handleBack} color="secondary" disabled={!!selectedRow}>
                            Back
                        </Button>
                        <LoadingButton
                            loading={loading}
                            onClick={
                                selectedRow?.status === 'SENT'
                                    ? handleResendInvites
                                    : handleSendInvites
                            }
                            autoFocus
                            variant="contained"
                            color="primary"
                            startIcon={<SendIcon />}
                        >
                            {selectedRow?.status === 'SENT'
                                ? 'Resend invitation'
                                : 'Send invitation'}
                        </LoadingButton>
                    </>
                )}
            </DialogActions>
        </Dialog>
    );
};

const CustomerInformationStep = ({
    customer,
    setCustomer,
}: {
    customer: CustomerInformation;
    setCustomer: (customer: CustomerInformation) => void;
}) => {
    return (
        <DialogContent>
            <DialogContentText>
                Invite customers to use PayLater by adding their information below.
            </DialogContentText>
            <Stack sx={{ my: 2, gap: 1 }}>
                <TextField
                    label="Customer email"
                    type="email"
                    fullWidth
                    value={customer?.email}
                    onChange={e => setCustomer({ ...customer, email: e.target.value })}
                    required
                />
                <TextField
                    label="Customer company name"
                    fullWidth
                    value={customer?.company_name}
                    onChange={e => setCustomer({ ...customer, company_name: e.target.value })}
                    required
                />
                <NumberFormat
                    customInput={TextField}
                    format="##-#######"
                    label="Customer EIN (optional)"
                    fullWidth
                    value={customer?.ein}
                    onChange={e => setCustomer({ ...customer, ein: e.target.value })}
                />
            </Stack>
        </DialogContent>
    );
};

const ReviewInvitationStep = ({
    customer,
    invitation,
}: {
    customer: CustomerInformation;
    invitation: OfferTermsInvitationDetail;
}) => {
    const [copied, setCopied] = useState(false);
    const handleCopy = () => {
        navigator.clipboard.writeText(invitation?.email_content || '');
        setCopied(true);
        setTimeout(() => {
            setCopied(false);
        }, 2000);
    };
    return (
        <DialogContent>
            <DialogContentText>Review invitation details below.</DialogContentText>
            <Box
                sx={{
                    py: 2,
                    display: 'flex',
                    flexDirection: {
                        xs: 'column-reverse',
                        md: 'row',
                    },
                    gap: 3,
                }}
            >
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 1,
                        flex: { md: 3 },
                    }}
                >
                    <Typography variant="subtitle1">Email preview</Typography>
                    <Box sx={{ position: 'relative', width: '100%', height: '100%' }}>
                        <TextField
                            value={invitation?.email_content}
                            inputProps={{
                                readOnly: true,
                            }}
                            multiline
                            rows={12}
                            sx={{
                                overflow: 'auto',
                                width: '100%',
                                height: '100%',
                            }}
                        />
                        <Button
                            color="primary"
                            variant="soft"
                            sx={{
                                backdropFilter: 'blur(8px)',
                                position: 'absolute',
                                bottom: 10,
                                right: 10,
                                zIndex: 100,
                            }}
                            onClick={handleCopy}
                            endIcon={copied ? <CheckIcon /> : <ContentCopyIcon />}
                        >
                            {copied ? 'Copied' : 'Copy'}
                        </Button>
                    </Box>
                </Box>
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 1,
                        flex: { md: 2 },
                    }}
                >
                    <Typography variant="subtitle1">Customer information</Typography>
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            p: 1,
                            border: '1px solid #e0e0e0',
                            borderRadius: 1,
                        }}
                    >
                        <Typography variant="body2">
                            <strong>Company:</strong>{' '}
                            {customer?.company_name || invitation?.company_name}
                        </Typography>
                        <Typography variant="body2">
                            <strong>Email:</strong> {customer?.email || invitation?.email}
                        </Typography>
                        {customer?.ein && (
                            <Typography variant="body2">
                                <strong>EIN:</strong> {customer?.ein}
                            </Typography>
                        )}
                    </Box>
                    <Alert severity="info">
                        We recommend you send through portal so you can see your invitee’s
                        application progress.
                    </Alert>
                </Box>
            </Box>
        </DialogContent>
    );
};
